const cartOfferTracking = (() => {
  const viewed = new Set();
  const observed = new Set();
  let drawerOpen = false;
  const allowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
    } catch {
      return false;
    }
  };
  const details = (form) => {
    if (!allowed()) return null;
    const card = form?.closest('[data-cart-offer]');
    const variant = form?.querySelector('[name="id"]')?.value;
    if (!card || !variant) return null;
    return {
      offer_type: card.dataset.cartOffer,
      offered_variant: String(variant),
      market: card.dataset.offerMarket,
      currency: card.dataset.offerCurrency,
      cart_context: card.dataset.offerCartContext,
      purchase_type: 'one_time',
    };
  };
  const publish = (name, data) => {
    if (!data || !allowed()) return false;
    try {
      if (typeof window.gtag !== 'function') return false;
      window.gtag('event', name, { ...data, send_to: 'G-NKK9LB9VRY' });
      return true;
    } catch {
      return false;
    }
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    if (!drawerOpen || document.visibilityState !== 'visible') return;
    for (const entry of entries) {
      if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
      const data = details(entry.target.querySelector('[data-cart-cross-sell-form="true"]'));
      if (!data) continue;
      const key = JSON.stringify(data);
      if (!viewed.has(key) && publish('cart_offer_view', data)) viewed.add(key);
    }
  }, { threshold: 0.5 }) : null;
  const scan = () => {
    if (!observer) return;
    for (const card of observed) {
      if (!card.isConnected) {
        observer.unobserve(card);
        observed.delete(card);
      }
    }
    document.querySelectorAll('[data-cart-drawer] [data-cart-offer]').forEach((card) => {
      if (observed.has(card)) return;
      observed.add(card);
      observer.observe(card);
    });
  };
  const refresh = () => {
    scan();
    for (const card of observed) {
      observer.unobserve(card);
      observer.observe(card);
    }
  };
  document.addEventListener('cart-is-open', () => {
    drawerOpen = true;
    refresh();
  });
  document.addEventListener('cart-will-close', () => { drawerOpen = false; });
  document.addEventListener('visitorConsentCollected', refresh);
  document.addEventListener('visibilitychange', refresh);
  document.addEventListener('DOMContentLoaded', scan);
  if (observer) new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  scan();
  return { details, add: (data) => publish('cart_offer_add', data) };
})();

document.addEventListener('change', async (event) => {
  const toggle = event.target.closest('[data-cart-refill-toggle]');

  if (!toggle || toggle.disabled) return;

  const control = toggle.closest('[data-cart-refill]');
  const errorMessage = control.querySelector('[data-cart-refill-error]');
  const previousState = !toggle.checked;

  toggle.disabled = true;
  control.setAttribute('aria-busy', 'true');
  errorMessage.hidden = true;
  errorMessage.textContent = '';

  try {
    const response = await fetch(`${window.theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: toggle.dataset.lineKey,
        quantity: Number(toggle.dataset.quantity),
        selling_plan: toggle.checked
          ? Number(toggle.dataset.sellingPlan)
          : null,
      }),
    });
    const cart = await response.json();

    if (!response.ok) {
      throw new Error(cart.description || window.theme.strings.cartError);
    }

    document.dispatchEvent(new CustomEvent('theme:update:cart'));
  } catch (error) {
    toggle.checked = previousState;
    toggle.disabled = false;
    control.setAttribute('aria-busy', 'false');
    errorMessage.textContent = error.message || window.theme.strings.cartError;
    errorMessage.hidden = false;
  }
});

// Cart cross-sell slider: keeps a visible progress track in sync with the
// horizontal card scroller, and lets the track be dragged.
(() => {
  const initSlider = (root) => {
    if (root.dataset.crossSellSlider === 'ready') return;

    const list = root.querySelector('[data-cross-sell-list]');
    const track = root.querySelector('[data-cross-sell-track]');
    const thumb = root.querySelector('[data-cross-sell-thumb]');
    const prev = root.querySelector('[data-cross-sell-prev]');
    const next = root.querySelector('[data-cross-sell-next]');

    if (!list || !track || !thumb) return;

    root.dataset.crossSellSlider = 'ready';

    const sync = () => {
      const overflow = list.scrollWidth - list.clientWidth;
      const ratio = list.clientWidth / list.scrollWidth;

      track.hidden = overflow <= 1;
      thumb.style.width = `${Math.max(ratio * 100, 12)}%`;

      const travel = track.clientWidth - thumb.offsetWidth;
      const progress = overflow > 0 ? list.scrollLeft / overflow : 0;
      thumb.style.transform = `translateX(${travel * progress}px)`;

      if (prev) prev.disabled = list.scrollLeft <= 1;
      if (next) next.disabled = list.scrollLeft >= overflow - 1;
    };

    const step = (direction) => {
      const card = list.querySelector('li');
      const distance = card ? card.offsetWidth + 12 : list.clientWidth;
      list.scrollBy({ left: distance * direction, behavior: 'smooth' });
    };

    if (prev) prev.addEventListener('click', () => step(-1));
    if (next) next.addEventListener('click', () => step(1));

    const scrollToPointer = (event) => {
      const bounds = track.getBoundingClientRect();
      const travel = bounds.width - thumb.offsetWidth;
      if (travel <= 0) return;

      const offset = event.clientX - bounds.left - thumb.offsetWidth / 2;
      const progress = Math.min(Math.max(offset / travel, 0), 1);
      list.scrollLeft = (list.scrollWidth - list.clientWidth) * progress;
    };

    list.addEventListener('scroll', sync, { passive: true });

    track.addEventListener('pointerdown', (event) => {
      track.setPointerCapture(event.pointerId);
      scrollToPointer(event);
    });

    track.addEventListener('pointermove', (event) => {
      if (!track.hasPointerCapture(event.pointerId)) return;
      scrollToPointer(event);
    });

    track.addEventListener('pointerup', (event) => {
      track.releasePointerCapture(event.pointerId);
    });

    if ('ResizeObserver' in window) {
      new ResizeObserver(sync).observe(list);
    } else {
      window.addEventListener('resize', sync);
    }

    sync();
  };

  const initAll = () => {
    document.querySelectorAll('.boosh-cross-sells').forEach(initSlider);
  };

  initAll();
  document.addEventListener('DOMContentLoaded', initAll);
  new MutationObserver(initAll).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();


document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-cart-cross-sell-form="true"]');
  if (!form) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const button = form.querySelector('button[type="submit"]');
  if (button.disabled) return;
  const errorMessage = form.querySelector('[data-cart-cross-sell-error]');
  button.disabled = true;
  form.setAttribute('aria-busy', 'true');
  errorMessage.hidden = true;
  try {
    const formData = new FormData(form);
    formData.set('sections', 'cart-items,cart-footer,cart-item-count,cart-live-region');
    formData.set('sections_url', window.location.pathname);
    const offerDetails = cartOfferTracking.details(form);
    const response = await fetch(`${window.theme.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || window.theme.strings.cartError);
    }
    cartOfferTracking.add(offerDetails);
    document.body.dispatchEvent(new CustomEvent('shapes:cart:afteradditem', {
      bubbles: true,
      detail: { response: result, sourceId: form.id, originalTarget: button },
    }));
  } catch (error) {
    errorMessage.textContent = error.message || window.theme.strings.cartError;
    errorMessage.hidden = false;
  } finally {
    button.disabled = false;
    form.setAttribute('aria-busy', 'false');
  }
}, true);

(() => {
  let removingCode = false;
  const closeInfo = () => {
    document.querySelectorAll('[data-cart-shipping-info-button]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      const popup = document.getElementById(button.getAttribute('aria-controls'));
      if (popup) popup.hidden = true;
    });
  };
  document.addEventListener('click', async (event) => {
    const infoButton = event.target.closest('[data-cart-shipping-info-button]');
    if (infoButton) {
      const open = infoButton.getAttribute('aria-expanded') !== 'true';
      closeInfo();
      infoButton.setAttribute('aria-expanded', String(open));
      document.getElementById(infoButton.getAttribute('aria-controls')).hidden = !open;
    } else if (!event.target.closest('[data-cart-shipping-info]')) {
      closeInfo();
    }
    const button = event.target.closest('[data-cart-remove-code]');
    if (!button || removingCode) return;
    removingCode = true;
    const summary = button.closest('[data-cart-summary]');
    const errorMessage = summary.querySelector('[data-cart-code-error]');
    const buttons = [...document.querySelectorAll('[data-cart-remove-code], [data-cart-refill-toggle], [name="checkout"][form="cart"]')];
    const previousDisabled = buttons.map((control) => control.disabled);
    buttons.forEach((control) => { control.disabled = true; });
    summary.setAttribute('aria-busy', 'true');
    errorMessage.hidden = true;
    document.dispatchEvent(new CustomEvent('shapes:cart:lock'));
    try {
      const root = window.Shopify.routes.root;
      const read = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!read.ok) throw new Error(window.theme.strings.cartError);
      const cart = await read.json();
      const applications = [
        ...(cart.cart_level_discount_applications || []),
        ...(cart.items || []).flatMap((item) => (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application)),
      ];
      const codes = Array.isArray(cart.discount_codes)
        ? cart.discount_codes.map((discount) => discount.code)
        : applications.filter((application) => application.type === 'discount_code').map((application) => application.title);
      const remaining = [...new Set(codes)].filter((code) => code.toLowerCase() !== button.dataset.cartRemoveCode.toLowerCase());
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: remaining.join(','), sections: 'cart-items,cart-footer,cart-item-count,cart-live-region', sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || window.theme.strings.cartError);
      if (!['cart-items', 'cart-footer', 'cart-item-count', 'cart-live-region'].every((section) => result.sections?.[section])) {
        document.dispatchEvent(new CustomEvent('theme:update:cart'));
        throw new Error(window.theme.strings.cartError);
      }
      document.body.dispatchEvent(new CustomEvent('shapes:cart:update', { bubbles: true, detail: { response: result } }));
    } catch (error) {
      errorMessage.textContent = error.message || window.theme.strings.cartError;
      errorMessage.hidden = false;
    } finally {
      buttons.forEach((control, index) => { control.disabled = previousDisabled[index]; });
      summary.removeAttribute('aria-busy');
      document.dispatchEvent(new CustomEvent('shapes:cart:unlock'));
      removingCode = false;
    }
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.querySelector('[data-cart-shipping-info-button][aria-expanded="true"]')) {
      closeInfo();
      event.stopPropagation();
    }
  }, true);
  document.addEventListener('focusout', (event) => { if (event.target.matches('[data-cart-shipping-info-button]')) closeInfo(); });
})();
